import { useCallback, useEffect, useRef, type ReactNode } from 'react'
import { useIntl } from 'react-intl'
import { NavigationType, useBlocker, useLocation, useNavigate, type BlockerFunction } from 'react-router'

import { isWithinPage } from '@/shared/details/page-visit'
import { Button, Dialog } from '@/shared/ui'
import { hasPendingEdits, usePendingEdits } from '@/shared/ui/pending-edits'

/**
 * Asks before leaving a form with unsaved changes: a dialog for a move within the app, the
 * browser's own prompt for a reload, a closed tab or another site. Render `prompt`; call
 * `allowNext` to let the next move through unasked, as after a save. It lapses once the form
 * changes again. A move whose state says it discards the changes (`isDiscardingChanges`), as a
 * Reload that opens the record again elsewhere, goes unasked; going Back or Forward to that page later
 * still asks, and the form it opens drops the mark at once. Typed text a field has not yet passed
 * to its form, such as a time still being typed, counts as a change (`setPendingEdit`).
 */
export function useUnsavedChangesPrompt(isFormDirty: boolean): {
  readonly prompt: ReactNode
  readonly allowNext: () => void
  /** A move away is being asked about, or was confirmed and is under way. */
  readonly isLeaving: boolean
} {
  const intl = useIntl()
  const hasPending = usePendingEdits()
  const dirty = isFormDirty || hasPending
  const dirtyRef = useRef(dirty)
  const allowed = useRef(false)
  useEffect(() => {
    dirtyRef.current = dirty
    if (dirty) {
      allowed.current = false
    }
  }, [dirty])

  const shouldBlock = useCallback<BlockerFunction>(({ currentLocation, nextLocation, historyAction }) => {
    if (allowed.current) {
      allowed.current = false
      return false
    }
    // Only the move that set the mark: Back or Forward to its page later still asks.
    if (historyAction !== NavigationType.Pop && isDiscarding(nextLocation.state)) {
      return false
    }
    // A change within the page, such as its open tab, leaves nothing.
    if (historyAction !== NavigationType.Pop && currentLocation.pathname === nextLocation.pathname && isWithinPage(nextLocation.state)) {
      return false
    }
    // Typed text is read at once, not as last rendered.
    return (
      (dirtyRef.current || hasPendingEdits()) &&
      (currentLocation.pathname !== nextLocation.pathname || currentLocation.search !== nextLocation.search)
    )
  }, [])
  const blocker = useBlocker(shouldBlock)
  const location = useLocation()
  const navigate = useNavigate()
  useEffect(() => {
    if (isDiscarding(location.state)) {
      void navigate({ pathname: location.pathname, search: location.search, hash: location.hash }, { replace: true, state: null })
    }
  }, [location, navigate])
  const allowNext = useCallback(() => {
    allowed.current = true
  }, [])

  useEffect(() => {
    if (!dirty) {
      return undefined
    }
    const ask = (event: BeforeUnloadEvent) => {
      event.preventDefault()
    }
    window.addEventListener('beforeunload', ask)
    return () => window.removeEventListener('beforeunload', ask)
  }, [dirty])

  const stay = useCallback(() => blocker.reset?.(), [blocker])
  const leave = useCallback(() => blocker.proceed?.(), [blocker])

  const prompt = (
    <Dialog
      open={blocker.state === 'blocked'}
      onClose={stay}
      title={intl.formatMessage({ id: 'forms.leave.title' })}
      size="xs"
      actions={
        <>
          <Button variant="outlined" onClick={stay}>
            {intl.formatMessage({ id: 'forms.leave.stay' })}
          </Button>
          <Button onClick={leave}>{intl.formatMessage({ id: 'forms.leave.leave' })}</Button>
        </>
      }
    >
      <p className="text-sm text-text-primary">{intl.formatMessage({ id: 'forms.leave.body' })}</p>
    </Dialog>
  )

  return { prompt, allowNext, isLeaving: blocker.state !== 'unblocked' }
}

// Whether a move's state says it discards the changes it leaves.
function isDiscarding(state: unknown): boolean {
  return (state as { readonly isDiscardingChanges?: boolean } | null)?.isDiscardingChanges === true
}
